if (!customElements.get('idw-product-experience')) {
  customElements.define('idw-product-experience', class extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.info = this.closest('product-info');
      if (!this.info || this.info.dataset.productId !== this.dataset.productId) return;
      try { this.config = JSON.parse(this.querySelector('[data-catalog-config]').textContent); } catch (_) { return; }
      this.labels = this.config.labels || {};
      this.active = true;
      this.moves = []; this.owned = []; this.tabs = []; this.groups = []; this.uploads = []; this.messages = [];
      this.titleEdits = []; this.decorated = [];
      this.appNodes = []; this.states = []; this.marked = new Set();
      this.originalPriceMode = this.info.getAttribute('data-idw-price-mode');
      this.events = new AbortController();
      const listen = (node, type, fn, capture = false) => node.addEventListener(type, fn, {signal:this.events.signal, capture});
      listen(this.info, 'input', () => this.schedule());
      listen(this.info, 'change', () => this.schedule());
      listen(this.info, 'idw:variant-request-state', () => this.schedule());
      listen(document, 'gpoRenderCompleted', () => { this.configChanged = true; this.schedule(); });
      listen(window, 'pageshow', () => this.schedule());
      listen(this.info, 'invalid', event => this.reveal(event.target), true);
      listen(this.info, 'click', event => {
        if (event.target.closest('button[name="add"],input[type="submit"]')) {
          this.validationRequested = true;
          this.groups.forEach(group => { group.details.open = true; });
          this.messages.forEach(message => { if (message.field.querySelector('[required],[presence]')) message.details.open = true; });
          this.schedule();
        }
      }, true);
      this.observer = new MutationObserver(() => this.schedule());
      this.observe();
      this.timers = [100,500,1500,4000,10000,30000].map(delay => setTimeout(() => this.schedule(), delay));
      this.schedule();
    }
    disconnectedCallback() {
      this.active = false;
      this.events?.abort(); this.observer?.disconnect();
      this.timers?.forEach(clearTimeout);
      cancelAnimationFrame(this.frame); this.frame = null;
      this.restore();
      if (this.info) {
        this.info.classList.remove('idw-catalog--ready');
        if (this.originalPriceMode == null) this.info.removeAttribute('data-idw-price-mode');
        else this.info.setAttribute('data-idw-price-mode', this.originalPriceMode);
      }
    }
    observe() {
      if (this.active) this.observer.observe(this.info, {childList:true, subtree:true, characterData:true, attributes:true,
        attributeFilter:['class','style','hidden','checked','disabled','aria-invalid','data-option-set-id','data-idw-variant-state']});
    }
    schedule() {
      if (!this.active || this.frame != null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.active || !this.isConnected) return;
        this.observer.disconnect();
        try { this.refresh(); } catch (error) {
          const message = String(error?.message || error);
          if (this.lastError !== message) console.warn('[IDworld product layout]', message);
          this.lastError = message;
          this.restore(); this.info.dataset.idwPriceMode = 'unknown';
        } finally { this.observe(); }
      });
    }
    make(tag, name, text) {
      const node = document.createElement(tag);
      if (name) node.className = `idw-catalog__${name}`;
      if (text) node.textContent = text;
      return node;
    }
    own(node) { this.owned.push(node); return node; }
    text(node, text) { if (node && node.textContent !== text) node.textContent = text; }
    flag(value) { return value === true || value === 1 || value === 'true' || value === '1'; }
    normalized(value) { return String(value || '').replace(/\s+/g, ' ').trim(); }
    displayTitle(value) { return this.normalized(value).replace(/^【([\s\S]*)】$/, '$1').trim(); }
    decorate(node, className) {
      if (!node.classList.contains(className)) { node.classList.add(className); this.decorated.push({node,className}); }
    }
    cleanTitle(node) {
      if (!node) return;
      const textNodes = [];
      const collect = parent => {
        for (const child of parent.childNodes) {
          if (child.nodeType === 3) textNodes.push(child);
          else if (child.nodeType === 1 && child.tagName !== 'SVG' && child.getAttribute('aria-hidden') !== 'true' && !/(?:^|[\s_-])(?:icon|arrow|chevron)(?:$|[\s_-])/i.test(child.className || '')) collect(child);
        }
      };
      collect(node);
      if (!/^\s*【[\s\S]*】\s*$/.test(textNodes.map(text => text.nodeValue).join(''))) return;
      const first = textNodes.find(text => text.nodeValue.includes('【'));
      const last = [...textNodes].reverse().find(text => text.nodeValue.includes('】'));
      for (const text of new Set([first,last])) {
        if (!text) continue;
        const before = text.nodeValue;
        let after = before;
        if (text === first) after = after.replace('【','');
        if (text === last) after = after.slice(0,after.lastIndexOf('】')) + after.slice(after.lastIndexOf('】') + 1);
        if (before !== after) { this.titleEdits.push({node:text,before,after}); text.nodeValue=after; }
      }
    }
    move(node, parent, before = null) {
      if (!this.moves.some(move => move.node === node)) {
        const marker = document.createComment('idw-catalog-origin'); node.before(marker);
        this.moves.push({node,marker});
      }
      if (node.parentNode !== parent || (before && node.nextSibling !== before)) parent.insertBefore(node, before);
    }
    restore() {
      this.layoutEvents?.abort();
      [...(this.titleEdits || [])].reverse().forEach(edit => { if (edit.node.nodeValue === edit.after) edit.node.nodeValue=edit.before; });
      this.decorated?.forEach(({node,className}) => node.classList.remove(className));
      this.marked?.forEach(node => node.removeAttribute('data-idw-catalog-redundant-value'));
      this.info?.querySelectorAll('[data-idw-catalog-discount-source]').forEach(node => node.removeAttribute('data-idw-catalog-discount-source'));
      this.groups?.forEach(group => group.title?.removeAttribute('data-idw-catalog-group-title'));
      this.tabs?.forEach(tab => {
        tab.tab.classList.toggle('gpo-tab-active', tab.active);
        tab.panel.classList.toggle('gpo-tabs-tabpanel-active', tab.panelActive);
        Object.entries(tab.attrs).forEach(([key,value]) => value == null ? tab.tab.removeAttribute(key) : tab.tab.setAttribute(key,value));
        if (tab.panelId == null) tab.panel.removeAttribute('id'); else tab.panel.id = tab.panelId;
      });
      [...(this.moves || [])].reverse().forEach(({node,marker}) => { if (marker.parentNode) marker.replaceWith(node); });
      this.owned?.forEach(node => node.remove());
      this.quantity?.classList.remove('idw-catalog__quantity');
      this.moves = []; this.owned = []; this.tabs = []; this.groups = []; this.uploads = []; this.messages = [];
      this.titleEdits = []; this.decorated = [];
      this.states = []; this.appNodes = []; this.marked = new Set();
      this.guide = this.discount = this.price = this.priceNote = this.quantity = this.form = null;
      this.discountSignature = null;
    }
    optionSet(app) {
      const id = app.dataset.optionSetId;
      const options = window.GPOConfigs?.options;
      if (!id || !options) return null;
      const set = options[id] || (Array.isArray(options) ? Object.values(options).find(item => item && String(item.id) === id) : null);
      return set && Array.isArray(set.elements) ? set : null;
    }
    conditional(element) {
      return this.flag(element?.conditionalField) && (element.clo?.whens || []).some(when => when.select && when.select !== 'null');
    }
    metadata(set) {
      const records = new Map(); const touched = new Set();
      const walk = (elements, ancestors = []) => {
        for (const element of elements || []) {
          if (!element || !element.id) continue;
          records.set(String(element.id), {element, ancestors});
          if (this.conditional(element)) {
            touched.add(String(element.id)); ancestors.forEach(item => touched.add(String(item.id)));
            (element.clo.whens || []).filter(when => when.select && when.select !== 'null').forEach(when => touched.add(String(when.select)));
          }
          if (Array.isArray(element.elements)) walk(element.elements, [...ancestors,element]);
        }
      };
      walk(set.elements);
      [...touched].forEach(id => records.get(id)?.ancestors.forEach(element => touched.add(String(element.id))));
      return {records,touched};
    }
    safe(record, state) {
      return Boolean(record) && !state.meta.touched.has(String(record.element.id)) && [record.element,...record.ancestors].every(element =>
        !this.conditional(element) &&
        (element.type !== 'group' || !element.style || element.style === 'default'));
    }
    field(state, id) {
      return [...state.app.querySelectorAll('[data-element-id]')].find(node => node.dataset.elementId === String(id));
    }
    priceMode(set) {
      if (!set) return 'unknown';
      let paid = false, unknown = false;
      const visit = (value, addon = false) => {
        if (!value || typeof value !== 'object') { if (addon && value) unknown = true; return; }
        if (value.addon_type === 'price') {
          const amount = Number(value.price);
          if (value.price == null || value.price === '' || !Number.isFinite(amount) || amount < 0) unknown = true;
          else if (amount > 0) paid = true;
        }
        if (value.addon_type && value.addon_type !== 'price' && !value.variant_id && value.price != null && value.price !== '' && Number(value.price) !== 0) unknown = true;
        if (value.variant_id) {
          const amount = Number(value.variant_price);
          if (value.variant_price == null || value.variant_price === '' || !Number.isFinite(amount) || amount < 0) unknown = true;
          else if (amount > 0) paid = true;
        } else if (addon && value.addon_type !== 'price' && value.price != null && value.price !== '') {
          const amount = Number(value.price);
          if (!Number.isFinite(amount) || amount < 0) unknown = true; else if (amount > 0) paid = true;
        }
        Object.entries(value).forEach(([key,item]) => {
          if (key === 'addon_product') visit(item,true);
          else if (item && typeof item === 'object') visit(item,addon && Array.isArray(value));
        });
      };
      visit(set.elements);
      return paid ? 'addon' : unknown ? 'unknown' : 'variant';
    }
    visible(node) {
      for (let current=node; current && current!==this.info; current=current.parentElement) {
        if (current.hidden || getComputedStyle(current).display === 'none') return false;
      }
      return Boolean(node?.isConnected);
    }
    pureGuide(field) {
      return Boolean(field) && !field.closest('details') &&
        !field.querySelector('input,select,textarea,form,iframe,object,embed,[contenteditable="true"],[role="textbox"],[role="combobox"]') &&
        ![...field.querySelectorAll('button')].some(button => !button.classList.contains('gpo-tabs-tab')) &&
        ![...field.querySelectorAll('*')].some(node => node.tagName.includes('-'));
    }
    setup(apps, form) {
      this.form = form; this.appNodes = apps;
      this.layoutEvents = new AbortController();
      this.states = apps.map(app => {
        const set = this.optionSet(app);
        return {app,set,meta:set ? this.metadata(set) : null};
      });
      for (const state of this.states) {
        const fields = [...state.app.querySelectorAll('[data-element-id]')];
        const files = fields.filter(field => field.querySelector('input[type="file"]') &&
          ![...field.querySelectorAll('[data-element-id]')].some(child => child.querySelector('input[type="file"]')));
        files.forEach(field => {
          const status = this.own(this.make('p','upload-status'));
          status.setAttribute('role','status'); status.setAttribute('aria-live','polite'); status.hidden = true;
          field.append(status); this.uploads.push({field,status});
        });
        if (!state.meta) continue;
        state.app.querySelectorAll('.gpo-label').forEach(label => this.cleanTitle(label.querySelector('.label-content') || label));
        const messages = [...state.meta.records.values()].filter(record => {
          const label = String(record.element.label_ja || record.element.label || '').replace(/[【】\[\]*＊]/g,'').trim();
          return record.element.type === 'textarea' && /^(?:メッセ[ー一]ジ|備考|ご要望)(?:[（(].*?[)）])?$/.test(label);
        });
        if (messages.length === 1 && files.length === 1) {
          const record = messages[0], field = this.field(state,record.element.id), upload = files[0];
          const uploadRecord = state.meta.records.get(upload.dataset.elementId);
          if (field && !this.flag(record.element.required) && !field.querySelector('[required],[presence]') && this.safe(record,state) &&
            this.safe(uploadRecord,state) && field.parentElement === upload.parentElement) {
            const details = this.own(this.make('details','message'));
            const summary = this.make('summary','summary',this.labels.message || record.element.label_ja || record.element.label);
            details.append(summary); upload.after(details); this.move(field,details);
            this.messages.push({field,details,summary});
          }
        }
        for (const record of state.meta.records.values()) {
          if (record.element.type !== 'tabs' || !this.safe(record,state)) continue;
          const field = this.field(state,record.element.id);
          if (!this.pureGuide(field)) continue;
          const tabs = [...field.querySelectorAll('.gpo-tabs-tab')], panels = [...field.querySelectorAll('.gpo-tabs-tabpanel')];
          if (!tabs.length || tabs.length !== panels.length) continue;
          const originParent = field.parentElement;
          this.ensureGuide(); this.move(field,this.guide);
          tabs.forEach((tab,index) => {
            const panel = panels[index];
            const saved = {tab,panel,field,state,originParent,active:tab.classList.contains('gpo-tab-active'),panelActive:panel.classList.contains('gpo-tabs-tabpanel-active'),panelId:panel.getAttribute('id'),
              attrs:Object.fromEntries(['role','tabindex','aria-expanded','aria-controls'].map(key => [key,tab.getAttribute(key)]))};
            this.tabs.push(saved);
            this.cleanTitle(tab);
            panel.id = `IdwCatalogGuide-${this.dataset.productId}-${this.dataset.formId}-${this.tabs.length}`;
            tab.classList.remove('gpo-tab-active'); panel.classList.remove('gpo-tabs-tabpanel-active');
            tab.setAttribute('role','button'); tab.setAttribute('tabindex','0'); tab.setAttribute('aria-controls',panel.id); tab.setAttribute('aria-expanded','false');
            tab.addEventListener('keydown', event => {
              if ((event.key === 'Enter' || event.key === ' ') && tab.tagName !== 'BUTTON') { event.preventDefault(); tab.click(); }
            }, {signal:this.layoutEvents.signal});
          });
        }
        this.prepareGuideContent(state);
        for (const record of state.meta.records.values()) {
          const element = record.element;
          if (element.type !== 'group' || !this.safe(record,state)) continue;
          const node = this.field(state,element.id);
          const label = this.normalized(element.label_ja || element.label);
          if (!node || !label || /^(?:group|グループ)(?:[\s_-]*\d+)?$/i.test(label) || node.closest('details')) continue;
          if (node.querySelector('details,input[type="file"],textarea,.gpo-tabs,.gpo-group')) continue;
          const descendants = [...state.meta.records.values()].filter(child => child.ancestors.includes(element));
          if (descendants.some(child => !this.safe(child,state) || !['radio','select','dropdown','image-dropdown','image-swatches','buttons','paragraph','heading','divider','spacing'].includes(child.element.type))) continue;
          const controls = [...node.querySelectorAll('input:not([type="hidden"]),select')];
          if (!controls.length) continue;
          const details = this.own(this.make('details','fold'));
          const summary = this.make('summary','summary');
          const values = this.make('span','values');
          summary.append(this.make('span','fold-title',this.displayTitle(label)),values,this.make('span','change',this.labels.change));
          const body = this.make('div','fold-body'); details.append(summary,body); node.before(details); this.move(node,body);
          details.open = descendants.some(child => this.flag(child.element.required) && !this.selected(this.field(state,child.element.id)).length);
          const title = [...node.children].find(child => child.classList.contains('gpo-group__title'));
          title?.setAttribute('data-idw-catalog-group-title','');
          this.groups.push({details,node,values,title});
        }
      }
    }
    ensureGuide() {
      if (this.guide) return;
      this.guide = this.own(this.make('section','guides'));
      if (this.labels.guide) this.guide.append(this.make('h2','heading',this.labels.guide));
      (this.form.closest('product-form') || this.form).after(this.guide);
    }
    prepareGuideContent(state) {
      const tabs = this.tabs.filter(tab => tab.state === state);
      if (!tabs.length) return;
      const whiteTabs = tabs.filter(tab => /白版|白インク/.test(this.normalized(tab.tab.textContent)));
      if (whiteTabs.length) {
        const first = whiteTabs[0];
        const whiteFields = [...new Set(tabs.filter(tab => tab.originParent === first.originParent && /白版|白インク|不透明度.*参考/.test(this.normalized(tab.tab.textContent))).map(tab => tab.field))].filter(field =>
          tabs.filter(tab => tab.field === field).every(tab => /白版|白インク|不透明度.*参考/.test(this.normalized(tab.tab.textContent))));
        let whiteGroup;
        for (const record of state.meta.records.values()) {
          if (record.element.type !== 'paragraph' || !this.safe(record,state)) continue;
          const paragraph = this.field(state,record.element.id);
          if (!this.pureGuide(paragraph) || paragraph.parentElement !== first.originParent) continue;
          const heading = paragraph.querySelector('h2,h3,h4');
          const title = this.displayTitle(heading?.textContent);
          if (!heading || !/白版|白インク/.test(title) || title.length > 45) continue;
          this.cleanTitle(heading);
          this.decorate(paragraph,'idw-catalog__guide-intro');
          if (whiteFields.includes(first.field)) {
            if (!whiteGroup) {
              whiteGroup = this.own(this.make('details','white-guide'));
              const summary = this.make('summary','summary');
              whiteGroup.append(summary); first.field.before(whiteGroup);
              this.move(heading,summary);
            }
            this.move(paragraph,whiteGroup);
          } else this.move(paragraph,this.guide,first.field);
        }
        if (whiteGroup) whiteFields.forEach(field => this.move(field,whiteGroup));
      }
      tabs.forEach(({panel}) => {
        panel.querySelectorAll('p,li').forEach(row => {
          const label = row.firstElementChild;
          if (!label || !['STRONG','B'].includes(label.tagName) || (!/[：:]\s*$/.test(label.textContent) && label.nextElementSibling?.tagName !== 'BR') || this.normalized(label.textContent).length > 32) return;
          if (row.querySelector('img,table,ul,ol,details,input,button,select,textarea') || this.normalized(row.textContent) === this.normalized(label.textContent)) return;
          const leadingText = [...row.childNodes].slice(0,[...row.childNodes].indexOf(label)).map(node => node.textContent).join('').trim();
          if (!leadingText) this.decorate(row,'idw-catalog__guide-fact');
        });
      });
      this.prepareSpecificationFacts(tabs);
      this.prepareKeyholderGuides(tabs);
      if (!this.labels.details) return;
      const serviceTabs = tabs.map(tab => {
        const title = this.displayTitle(tab.tab.textContent);
        const heading = this.displayTitle(tab.panel.querySelector('h2,h3,h4')?.textContent);
        const free = /無料/.test(title + heading), paid = /有料/.test(title + heading);
        return {saved:tab,title,cost:free && paid ? 'conflict' : free ? 'free' : paid ? 'paid' : 'unspecified'};
      }).filter(tab => /データ.*調整|おまかせ.*(?:制作|調整)/.test(tab.title));
      for (const record of state.meta.records.values()) {
        const element = record.element;
        if (!['switch','checkbox','radio','select','dropdown'].includes(element.type)) continue;
        const label = this.displayTitle(element.label_ja || element.label);
        if (!/データ.*調整|おまかせ.*(?:制作|調整)/.test(label)) continue;
        const field = this.field(state,element.id);
        if (!field || !field.querySelector('input,select')) continue;
        const pricing = this.priceMode({elements:[element]});
        if (/無料/.test(label) && pricing !== 'variant') continue;
        const cost = /無料/.test(label) && pricing === 'variant' ? 'free' : /有料/.test(label) || pricing === 'addon' ? 'paid' : 'unspecified';
        if (cost === 'unspecified') continue;
        const key = this.serviceKey(label);
        let candidates = serviceTabs.filter(tab => tab.cost === cost && key && this.serviceKey(tab.title) === key);
        if (!candidates.length) {
          // Merchant guides often omit (無料)/(有料). Match their service name,
          // but never share an unpriced guide between conflicting service costs.
          const conflict = [...state.meta.records.values()].some(other => {
            if (other === record || !['switch','checkbox','radio','select','dropdown'].includes(other.element.type)) return false;
            const otherLabel = this.displayTitle(other.element.label_ja || other.element.label);
            if (!key || this.serviceKey(otherLabel) !== key) return false;
            const otherPricing = this.priceMode({elements:[other.element]});
            return cost === 'free' ? /有料/.test(otherLabel) || otherPricing !== 'variant' : /無料/.test(otherLabel);
          });
          if (!conflict) candidates = serviceTabs.filter(tab => tab.cost === 'unspecified' && key && this.serviceKey(tab.title) === key);
        }
        if (candidates.length !== 1) continue;
        const target = candidates[0].saved;
        const button = this.own(this.make('button','guide-link',this.labels.details));
        button.type='button'; button.classList.add('idw-catalog__service-link');
        button.setAttribute('aria-controls',target.panel.id);
        button.setAttribute('aria-label',`${label} — ${this.labels.details}`);
        button.addEventListener('click',() => this.openGuide(target),{signal:this.layoutEvents.signal});
        field.append(button);
      }
    }
    prepareSpecificationFacts(tabs) {
      // Only the specification topic uses aligned facts. Keep all merchant nodes,
      // including text, links and deliberate breaks, reversible through restore().
      const titles = new Set(['商品仕様','アクリルブロックの仕様について','ダイカットアクリルブロックの仕様について','ゆらゆらアクリルブロックの仕様について','二層アクリルブロックの仕様について']);
      const excluded = 'ul,ol,table,img,svg,video,audio,canvas,p,div,section,article,figure,h1,h2,h3,h4,h5,h6,blockquote,pre,script,style,details,input,button,select,textarea,form,iframe,object,embed,[contenteditable]';
      tabs.forEach(({tab,panel}) => {
        if (!titles.has(this.displayTitle(tab.textContent).replace(/\u200b/g,''))) return;
        panel.querySelectorAll('li,p').forEach(row => {
          if (row.classList.contains('idw-spec-fact') || row.parentElement?.closest('li') || row.querySelector(excluded)) return;
          const label = row.querySelector('strong,b');
          if (!label || !/[：:]\s*$/.test(label.textContent) || this.normalized(label.textContent).length > 32 || this.normalized(row.textContent) === this.normalized(label.textContent)) return;
          // The label may be inside transparent rich-text spans, but not inside
          // a link or another semantic element whose meaning would be altered.
          let ancestor = label.parentElement;
          while (ancestor && ancestor !== row) {
            if (ancestor.tagName !== 'SPAN') return;
            ancestor = ancestor.parentElement;
          }
          const prefix = [];
          let found = false, leadingText = '';
          const visit = node => {
            if (node === label) { found = true; return; }
            if (found) return;
            if (node.nodeType === 3) { prefix.push(node); leadingText += node.textContent; }
            else for (const child of node.childNodes) visit(child);
          };
          visit(row);
          if (!found || this.normalized(leadingText)) return;
          const original = [...row.childNodes];
          const grid = this.own(document.createElement('span'));
          const labelBox = this.own(document.createElement('span'));
          const valueBox = this.own(document.createElement('span'));
          grid.className = 'idw-spec-fact__grid';
          labelBox.className = 'idw-spec-fact__label';
          valueBox.className = 'idw-spec-fact__value';
          grid.append(labelBox,valueBox); row.append(grid);
          original.forEach(node => this.move(node,valueBox));
          prefix.forEach(node => this.move(node,labelBox));
          this.move(label,labelBox);
          // Hide only a leading presentation break after the label. Later breaks
          // in the value remain visible, for example a second size instruction.
          let first = null;
          const firstLeaf = node => {
            if (first) return;
            if (node.nodeType === 3) { if (this.normalized(node.textContent)) first = node; }
            else if (node.nodeType === 1) {
              if (node.tagName === 'BR') first = node;
              else for (const child of node.childNodes) firstLeaf(child);
            }
          };
          firstLeaf(valueBox);
          if (first?.nodeType === 1 && first.tagName === 'BR') this.decorate(first,'idw-spec-fact__leading-break');
          this.decorate(row,'idw-spec-fact');
        });
      });
    }
    prepareKeyholderGuides(tabs) {
      // Presentation only: the merchant's rich text, links and native controls
      // remain their original nodes. Every wrapper/move is undone by restore().
      if (this.dataset.productId !== '7047119962176') return;
      const kinds = new Map([
        ['ファイル要件','requirements'],['商品仕様','specs'],['テンプレート','template'],
        ['送料','shipping'],['入稿データおまかせ調整','adjustment'],
        ['サイズ調整について','size'],['カットパスを自作する場合のご案内','cut']
      ]);
      const excluded = 'input,button,select,textarea,form,iframe,object,embed,[contenteditable="true"]';
      const wrap = (nodes,className) => {
        if (!nodes.length || nodes.some(node => node.parentElement !== nodes[0].parentElement)) return null;
        const box = this.own(document.createElement('div'));
        box.className = className; nodes[0].before(box);
        nodes.forEach(node => this.move(node,box));
        return box;
      };
      const fact = row => {
        if (row.classList.contains('idw-kh-fact-row') || row.classList.contains('idw-spec-fact') || row.querySelector(excluded + ',img,table,ul,ol,details')) return;
        const label = row.querySelector('strong,b');
        if (!label || this.normalized(label.textContent).length > 32 || this.normalized(row.textContent) === this.normalized(label.textContent)) return;
        let reached = false, before = '';
        const visit = node => {
          if (node === label) { reached = true; return; }
          if (reached) return;
          if (node.nodeType === 3) before += node.textContent;
          else for (const child of node.childNodes) visit(child);
        };
        visit(row);
        if (this.normalized(before)) return;
        const original = [...row.childNodes];
        const labelBox = this.own(document.createElement('span'));
        const valueBox = this.own(document.createElement('span'));
        labelBox.className = 'idw-kh-fact-label'; valueBox.className = 'idw-kh-fact-value';
        row.append(labelBox,valueBox);
        original.forEach(node => this.move(node,valueBox));
        this.move(label,labelBox);
        // A merchant-authored break after the label is unnecessary in columns.
        const first = [...valueBox.childNodes].find(node => node.nodeType === 1 || (node.nodeType === 3 && this.normalized(node.textContent)));
        if (first?.nodeType === 1 && first.tagName === 'BR') this.decorate(first,'idw-kh-empty');
        this.decorate(row,'idw-kh-fact-row');
      };
      tabs.forEach(({tab,panel}) => {
        if (!tab.classList.contains('idw-kh-guide-trigger')) {
          this.decorate(tab,'idw-kh-guide-trigger');
          const signal = this.layoutEvents.signal;
          tab.addEventListener('click',event => {
            // Globo keeps ownership of opening/closing panels. Preserve the
            // clicked heading only when its native switch moves it offscreen.
            // Programmatic openGuide() calls already manage their own scroll.
            if (!event.isTrusted) return;
            requestAnimationFrame(() => {
              if (signal.aborted || !this.active || !this.isConnected || !tab.isConnected) return;
              const rect = tab.getBoundingClientRect();
              const top = parseFloat(getComputedStyle(tab).scrollMarginTop) || 0;
              const height = window.innerHeight || document.documentElement.clientHeight;
              if (rect.top < top || rect.bottom > height) tab.scrollIntoView({block:'start',behavior:'auto'});
            });
          },{signal});
        }
        const kind = kinds.get(this.displayTitle(tab.textContent));
        if (!kind || panel.classList.contains('idw-kh-guide--' + kind) || panel.querySelector(excluded)) return;
        this.decorate(panel,'idw-kh-guide--' + kind);
        if (kind === 'specs') panel.querySelectorAll('li').forEach(fact);
        if (kind === 'requirements') {
          const list = panel.querySelector('ol');
          if (list) [...list.children].filter(node => node.tagName === 'LI').slice(0,3).forEach(fact);
        }
        if (kind === 'shipping') panel.querySelectorAll('li').forEach(row => {
          const lineBreak = [...row.children].find(node => node.tagName === 'BR');
          if (!lineBreak) return;
          const nodes = [...row.childNodes].slice([...row.childNodes].indexOf(lineBreak) + 1);
          if (!/沖縄|離島/.test(this.normalized(nodes.map(node => node.textContent).join('')))) return;
          const note = this.own(document.createElement('span'));
          note.className = 'idw-kh-shipping-note'; row.append(note);
          nodes.forEach(node => this.move(node,note));
          this.decorate(lineBreak,'idw-kh-empty');
        });
        if (kind === 'size' || kind === 'cut') {
          const paragraphs = [...panel.querySelectorAll('p')];
          paragraphs.forEach(paragraph => {
            if (!this.normalized(paragraph.textContent) && !paragraph.querySelector('img,svg,video,audio,canvas')) this.decorate(paragraph,'idw-kh-empty');
          });
          if (kind === 'size') {
            paragraphs.filter(paragraph => /^▼\s*(?:調整例|ご指定方法)/.test(this.normalized(paragraph.textContent))).forEach(paragraph => this.decorate(paragraph,'idw-kh-subheading'));
            paragraphs.filter(paragraph => /^[12][.．]\s*/.test(this.normalized(paragraph.textContent))).forEach(start => {
              this.decorate(start,'idw-kh-subheading');
              const members = [start];
              let next = start.nextElementSibling;
              while (next?.tagName === 'P' && this.normalized(next.textContent) && !/^(?:▼|[0-9]+[.．])/.test(this.normalized(next.textContent))) {
                members.push(next); next = next.nextElementSibling;
              }
              if (members.length > 1) wrap(members,'idw-kh-method');
            });
          } else {
            const start = paragraphs.find(paragraph => /^⑤/.test(this.normalized(paragraph.textContent)));
            if (start) {
              const members = [start];
              let next = start.nextElementSibling;
              while (next?.tagName === 'P' && this.normalized(next.textContent) && !/^[①-⑳0-9▼※]/.test(this.normalized(next.textContent))) {
                members.push(next); next = next.nextElementSibling;
              }
              if (members.length > 1) wrap(members,'idw-kh-continuation');
            }
          }
        }
        this.prepareKeyholderGuideDetails(panel,kind);
      });
    }
    prepareKeyholderGuideDetails(panel,kind) {
      const content = panel.querySelector('.gpo-tabs-tabcontent') || panel;
      const element = (tag,className,text) => {
        const node = this.own(document.createElement(tag));
        node.className = className;
        if (text) node.textContent = text;
        return node;
      };
      const stripPrefix = (root,pattern) => {
        const walk = node => {
          if (node.nodeType === 3 && this.normalized(node.nodeValue)) {
            const before = node.nodeValue, after = before.replace(pattern,'');
            if (before !== after) {
              this.titleEdits.push({node,before,after}); node.nodeValue = after;
            }
            return true;
          }
          return [...node.childNodes].some(walk);
        };
        walk(root);
      };
      if (kind === 'size') {
        const paragraphs = [...content.querySelectorAll('p')];
        const range = paragraphs.find(p => /標準サイズ/.test(p.textContent) && /1mm/.test(p.textContent));
        if (range) range.before(element('h4','idw-kh-range-title','調整できる範囲'));
        paragraphs.filter(p => /^▼/.test(this.normalized(p.textContent))).forEach(p => stripPrefix(p,/^\s*▼\s*/));
      }
      if (kind === 'cut') {
        const rules = [...content.querySelectorAll('p')].filter(p => /^[①-⑤]/.test(this.normalized(p.textContent)));
        if (rules.length !== 5) return;
        const members = rules.map(p => p.closest('.idw-kh-continuation') || p);
        if (members.some(p => p.parentElement !== members[0].parentElement)) return;
        const list = element('ul','idw-kh-cut-rules');
        members[0].before(list);
        members.forEach((node,index) => {
          stripPrefix(rules[index],/^\s*[①-⑤]\s*/);
          const item = element('li','idw-kh-cut-rule');
          list.append(item); this.move(node,item);
        });
      }
      if (kind !== 'adjustment' || content.querySelector('.idw-kh-service-details')) return;
      const sectionList = title => {
        const heading = [...content.querySelectorAll('h4')].find(h => this.normalized(h.textContent) === title);
        const list = heading?.nextElementSibling;
        return list && /^(UL|OL)$/.test(list.tagName) ? [...list.children].filter(n => n.tagName === 'LI') : [];
      };
      const supported = sectionList('対応内容');
      const excluded = sectionList('対応できない内容');
      const flow = sectionList('ご利用の流れ');
      const intro = [...content.querySelectorAll('p')].find(p => /サポートサービス/.test(p.textContent));
      // Unknown merchant layouts stay fully visible instead of hiding essential copy.
      if (!intro || supported.length < 3 || excluded.length < 4 || flow.length < 4) return;
      const original = [...content.childNodes];
      const brief = element('div','idw-kh-service-brief');
      brief.append(element('p','idw-kh-service-intro',this.normalized(intro.textContent)));
      const facts = element('ul','idw-kh-service-facts');
      const addFact = (label,text) => {
        const row = element('li','idw-kh-service-fact');
        row.append(element('strong','idw-kh-service-label',label + '：'),document.createTextNode(text));
        facts.append(row);
      };
      addFact('対応内容',supported.map(n => this.normalized(n.textContent)).join('、'));
      addFact('主な対象外',[excluded[0],excluded[3],excluded.find(n => /複雑/.test(n.textContent))].filter(Boolean).map(n => this.normalized(n.textContent)).join('、') + 'など');
      // Keep customer actions and preview confirmation; staff processing remains in full detail.
      const customerSteps = flow.filter(n => !/^スタッフ/.test(this.normalized(n.textContent)));
      const compactStep = node => this.normalized(node.textContent)
        .replace(/^商品ページで「[^」]+」をご選択ください。$/,'サービスを選択')
        .replace(/^(.+)してください。$/,'$1')
        .replace(/^.*ご希望がある場合は(.+)へご記入ください。$/,'希望内容を$1に記入')
        .replace(/^(.+)をご確認いただいた後、制作へ進行いたします。$/,'$1を確認後、制作');
      addFact('ご利用の流れ',customerSteps.map(compactStep).join(' → '));
      brief.append(facts);
      const details = element('details','idw-kh-service-details');
      const summary = element('summary','idw-kh-service-toggle','対応範囲・ご利用の流れを詳しく見る');
      const body = element('div','idw-kh-service-full');
      details.append(summary,body);
      content.append(brief,details);
      original.forEach(node => this.move(node,body));
      summary.addEventListener('click',() => {
        if (!details.open) return;
        requestAnimationFrame(() => {
          if (!this.active || !summary.isConnected) return;
          const top = parseFloat(getComputedStyle(summary).scrollMarginTop) || 0;
          if (summary.getBoundingClientRect().top < top) summary.scrollIntoView({block:'start',behavior:'auto'});
        });
      },{signal:this.layoutEvents.signal});
    }
    serviceKey(value) {
      return this.displayTitle(value).replace(/[（(](?:無料|有料)[)）]/g,'').replace(/(?:について|のご案内|の詳細|詳細)$/,'').replace(/\s+/g,'');
    }
    openGuide(saved) {
      if (!saved.tab.isConnected || !saved.panel.isConnected) return;
      this.reveal(saved.tab);
      if (!saved.tab.classList.contains('gpo-tab-active')) saved.tab.click();
      if (!saved.tab.classList.contains('gpo-tab-active')) {
        this.tabs.filter(tab => tab.field === saved.field).forEach(tab => {
          const active = tab === saved;
          tab.tab.classList.toggle('gpo-tab-active',active);
          tab.panel.classList.toggle('gpo-tabs-tabpanel-active',active);
          tab.tab.setAttribute('aria-expanded',String(active));
        });
      }
      saved.tab.setAttribute('aria-expanded','true');
      saved.tab.focus({preventScroll:true});
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      saved.tab.scrollIntoView({block:'start',behavior:reduced ? 'auto' : 'smooth'});
    }
    selected(node) {
      if (!node) return [];
      return [...node.querySelectorAll('input:checked,select')].filter(input => input.tagName === 'SELECT' || ['radio','checkbox'].includes(input.type)).map(input => {
        if (input.tagName === 'SELECT') return [...input.options].filter(option => option.selected).map(option => option.textContent.trim()).join('、');
        return input.value;
      }).filter(Boolean);
    }
    updateQuantity(form) {
      const buttons = form.querySelector('.product-form__buttons');
      const quantity = this.info.querySelector('.product-form__quantity');
      if (!buttons || !quantity) return;
      this.quantity = quantity; quantity.classList.add('idw-catalog__quantity');
      this.move(quantity,buttons.parentNode,buttons);
      if (!this.price) {
        this.price = this.own(this.make('div','price'));
        this.price.append(this.make('span','price-label'),this.make('strong','price-value'));
        this.priceNote = this.own(this.make('p','price-note'));
      }
      if (this.price.parentElement !== quantity) quantity.append(this.price);
      if (this.priceNote.parentElement !== quantity) quantity.append(this.priceNote);
      const variantId = form.querySelector('[name="id"]')?.value;
      const amount = this.config.prices?.[variantId];
      const mode = this.info.dataset.idwPriceMode;
      const unconfirmedOptionPrice = ['7159141204032','7298568650816','7304443527232'].includes(this.dataset.productId) && !this.info.classList.contains('idw-volume-pricing--ready');
      const label = unconfirmedOptionPrice ? '基本単価（税込・数量割引／追加料金適用前）' : mode === 'variant' ? this.labels.unitPrice : mode === 'addon' ? this.labels.basePrice : (this.labels.basePriceUnknown || this.labels.basePrice);
      this.text(this.price.firstElementChild,label || '');
      let price = '—';
      if (!['loading','error'].includes(this.info.dataset.idwVariantState) && typeof amount === 'number' && Number.isFinite(amount) && amount >= 0) {
        price = new Intl.NumberFormat(this.config.locale || 'ja', {style:'currency',currency:this.config.currency || 'JPY'}).format(amount / 100);
      }
      this.text(this.price.lastElementChild,price);
      this.price.hidden = this.info.classList.contains('idw-volume-pricing--ready');
      this.text(this.priceNote,unconfirmedOptionPrice ? '数量割引・追加料金を含む金額はカート・決済画面でご確認ください。' : this.labels.addonNote || '');
      this.priceNote.hidden = unconfirmedOptionPrice ? false : mode !== 'addon' || !this.labels.addonNote;
    }
    updateUploads() {
      for (const {field,status} of this.uploads) {
        const uploading = Boolean(field.querySelector('.gpo-uploading'));
        const count = field.querySelectorAll('.gpo-f-card').length;
        const error = [...field.querySelectorAll('.messages')].some(message => message.textContent.trim() && this.visible(message));
        const state = uploading ? 'uploading' : error ? 'error' : count ? 'complete' : 'empty';
        const text = state === 'uploading' ? this.labels.uploadInProgress : state === 'error' ? this.labels.uploadAttention : count ? String(this.labels.uploadComplete || '').replace('__COUNT__',count) : '';
        status.hidden = state === 'empty' || !this.visible(field); status.dataset.state = state; this.text(status,text || '');
      }
    }
    updateDiscount() {
      if (this.dataset.pricingEnabled === 'false') {
        this.info.querySelectorAll('[data-idw-catalog-discount-source]').forEach(node => node.removeAttribute('data-idw-catalog-discount-source'));
        if (this.discount) this.discount.hidden = true;
        return;
      }
      const widgets = [...this.info.querySelectorAll('.bndlr-volume')].filter(node => !node.closest('.idw-catalog__discount'));
      const valid = widgets.filter(widget => widget.querySelector('.bndlr-volume-title')?.textContent.trim() && widget.querySelector('.bndlr-volume-discount'));
      if (this.info.classList.contains('idw-volume-pricing--ready') || valid.length !== 1) {
        widgets.forEach(widget => widget.removeAttribute('data-idw-catalog-discount-source'));
        if (this.discount) this.discount.hidden = true;
        return;
      }
      const widget = valid[0];
      const title = widget.querySelector('.bndlr-volume-title').textContent.trim();
      const description = widget.querySelector('.bndlr-volume-description')?.textContent.trim() || '';
      const rows = [...widget.querySelectorAll('.bndlr-volume-discount')].map(row => row.textContent.trim()).filter(Boolean);
      if (!rows.length) return;
      if (!this.discount) {
        this.discount = this.own(this.make('details','discount'));
        this.discount.append(this.make('summary','summary'),this.make('div','discount-content'));
      }
      const after = this.form.closest('product-form') || this.form;
      if (after.nextElementSibling !== this.discount) after.after(this.discount);
      const signature = JSON.stringify([title,description,rows]);
      if (signature !== this.discountSignature) {
        this.discountSignature = signature; this.text(this.discount.firstElementChild,title);
        const list = this.make('ul','discount-list');
        [...widget.querySelectorAll('.bndlr-volume-discount')].filter(row => row.textContent.trim()).forEach(row => {
          const item = this.make('li'); this.copyDiscountMerchant(row,item); list.append(item);
        });
        const content = this.discount.lastElementChild; content.replaceChildren();
        if (description) { const lead=this.make('p','upload-hint'); this.copyDiscountMerchant(widget.querySelector('.bndlr-volume-description'),lead); content.append(lead); }
        content.append(list);
      }
      widgets.filter(item => item !== widget).forEach(item => item.removeAttribute('data-idw-catalog-discount-source'));
      widget.setAttribute('data-idw-catalog-discount-source',''); this.discount.hidden = false;
    }
    copyDiscountMerchant(source, target) {
      this.copyMerchant(source,target);
      let optional;
      try { optional = JSON.parse(this.info.querySelector('idw-volume-pricing-pilot [data-volume-config]').textContent).labels?.consultationOptional || '大口注文や納期についてのご相談も承ります。'; } catch (_) { return; }
      if (typeof optional !== 'string' || !optional.trim() || /translation missing/i.test(optional)) return;
      const nodes = [];
      let offset = 0;
      const collect = parent => {
        for (const node of parent.childNodes) {
          if (node.nodeType === 3) {
            nodes.push({node,start:offset,end:offset + node.textContent.length});
            offset += node.textContent.length;
          } else if (node.nodeType === 1) collect(node);
        }
      };
      collect(target);
      const text = nodes.map(entry => entry.node.textContent).join('');
      const edits = [];
      const span = (start,end) => nodes.filter(entry => entry.start < end && entry.end > start);
      const links = entries => [...new Set(entries.map(entry => entry.node.parentElement?.closest('a')).filter(Boolean))];
      const contact = /((?:\{\{quantity\}\}|[0-9０-９][0-9０-９,，]*)\s*(?:個|セット)以上)(の場合は、|は)(メール(?:で|にて)(?:ご連絡|お問い合わせ)ください[。]?|必ず担当者へご連絡ください[。]?)/g;
      for (const match of text.matchAll(contact)) {
        const start = match.index + match[1].length;
        const bodyStart = start + match[2].length;
        const end = match.index + match[0].length;
        if (links(span(bodyStart,end)).length > 1) continue;
        edits.push({start,end:bodyStart,value:'：'}, {start:bodyStart,end,value:optional});
      }
      for (const match of text.matchAll(/大量注文は要相談[。]?/g)) {
        const start = match.index, end = start + match[0].length;
        if (links(span(start,end)).length <= 1) edits.push({start,end,value:optional});
      }
      edits.sort((a,b) => b.start - a.start).forEach(edit => {
        const entries = span(edit.start,edit.end);
        const anchor = links(entries)[0];
        const destination = anchor ? entries.find(entry => entry.node.parentElement?.closest('a') === anchor) : entries[0];
        for (const entry of entries) {
          const start = Math.max(0,edit.start - entry.start);
          const end = Math.min(entry.end,edit.end) - entry.start;
          const value = entry.node.textContent;
          entry.node.textContent = value.slice(0,start) + (entry === destination ? edit.value : '') + value.slice(end);
        }
      });
    }
    copyMerchant(source, target) {
      if (!source) return;
      for (const node of source.childNodes) {
        if (node.nodeType === 3) { target.append(document.createTextNode(node.textContent)); continue; }
        if (node.nodeType !== 1 || ['SCRIPT','STYLE','IFRAME','OBJECT'].includes(node.tagName)) continue;
        if (node.tagName === 'BR') { target.append(document.createElement('br')); continue; }
        let child = target;
        const href = node.tagName === 'A' ? node.getAttribute('href') : null;
        if (href && /^(?:https?:|mailto:|tel:|\/|#)/i.test(href.trim())) {
          child = document.createElement('a'); child.setAttribute('href',href);
          if (node.getAttribute('target') === '_blank') { child.target='_blank'; child.rel='noopener noreferrer'; }
          target.append(child);
        } else if (['STRONG','B','EM'].includes(node.tagName)) { child=document.createElement(node.tagName.toLowerCase()); target.append(child); }
        this.copyMerchant(node,child);
      }
    }
    refresh() {
      const form = [...this.info.querySelectorAll('form')].find(node => node.getAttribute('id') === this.dataset.formId);
      if (!form) return;
      const apps = [...this.info.querySelectorAll('.gpo-app[data-option-set-id]')];
      const changed = this.form !== form || apps.length !== this.appNodes.length || apps.some((app,index) => app !== this.appNodes[index]) ||
        this.states.some(state => this.optionSet(state.app) !== state.set) || this.moves.some(move => !move.marker.isConnected) ||
        this.uploads.some(upload => !upload.field.isConnected) || this.configChanged;
      if (changed) { this.restore(); this.configChanged = false; this.setup(apps,form); }
      this.info.classList.add('idw-catalog--ready');
      const modes = this.states.map(state => this.priceMode(state.set));
      let mode = modes.includes('addon') ? 'addon' : modes.length && modes.every(value => value === 'variant') ? 'variant' : 'unknown';
      if (!apps.length && this.config.optionsExpected === false) mode = 'variant';
      for (const app of apps) {
        for (const control of app.querySelectorAll('[data-addon-price]')) {
          const value = control.getAttribute('data-addon-price');
          if (value && Number(value) > 0) mode = 'addon';
          else if (value && (!Number.isFinite(Number(value)) || Number(value) < 0) && mode !== 'addon') mode = 'unknown';
        }
        app.querySelectorAll('.gpo-selected-values').forEach(value => {
          const field = value.closest('[data-element-id]');
          const button = field?.querySelector('.gpo-dropdown .dropdown-button__value');
          const redundant = button && this.normalized(value.textContent) && this.normalized(value.textContent) === this.normalized(button.textContent);
          if (redundant) { value.setAttribute('data-idw-catalog-redundant-value',''); this.marked.add(value); }
          else value.removeAttribute('data-idw-catalog-redundant-value');
        });
      }
      if (this.info.dataset.idwPriceMode !== mode) this.info.dataset.idwPriceMode = mode;
      this.updateQuantity(form); this.updateUploads();
      this.messages.forEach(message => this.text(message.summary,message.field.querySelector('textarea')?.value.trim() ? this.labels.messageAdded : this.labels.message));
      this.groups.forEach(group => this.text(group.values,this.selected(group.node).join(' ／ ')));
      this.tabs.forEach(tab => {
        const expanded = String(tab.tab.classList.contains('gpo-tab-active'));
        if (tab.tab.getAttribute('aria-expanded') !== expanded) tab.tab.setAttribute('aria-expanded',expanded);
      });
      this.updateDiscount();
      if (this.validationRequested && !this.uploads.some(upload => upload.field.querySelector('.gpo-uploading'))) {
        const error = [...this.info.querySelectorAll('.gpo-element .messages')].find(node => node.textContent.trim() && this.visible(node));
        if (error) { this.reveal(error.closest('.gpo-element')); this.validationRequested = false; }
      }
    }
    reveal(node) {
      if (!node) return;
      for (let parent=node; parent && parent!==this.info; parent=parent.parentElement) {
        if (parent.tagName === 'DETAILS') parent.open = true;
      }
      const control = node.matches('input:not([type="hidden"]),select,textarea,button') ? node :
        [...node.querySelectorAll('input:not([type="hidden"]),select,textarea,button,[tabindex]')].find(item => !item.disabled && this.visible(item));
      control?.focus({preventScroll:true});
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      node.scrollIntoView({block:'center',behavior:reduced ? 'auto' : 'smooth'});
    }
  });
}


